export type CtaImageLayout = 'banner' | 'side';
export type CtaImageCardTarget = 'compact' | 'regular';

interface CtaImageSlotGeometry {
  width: number;
  height: number;
}

interface CtaImageCardGeometry {
  banner: CtaImageSlotGeometry;
  side: CtaImageSlotGeometry;
  contentHeight: number;
  minimumReadableShortSide: number;
  fallbackLayout: CtaImageLayout;
}

/**
 * Reference geometry for the 384px-wide end-screen card.
 *
 * Banner and side media slots keep the same 1200:350 and 600:520 ratios as the generated image
 * variants. Compact uses a representative 320px card; regular uses the current 384px max width.
 * Content heights model the existing text block, including the extra wrapping caused by a compact
 * side slot. These fixed targets make layout selection stable before the card is rendered.
 */
const CARD_GEOMETRY: Record<CtaImageCardTarget, CtaImageCardGeometry> = {
  compact: {
    banner: { width: 320, height: (320 * 350) / 1200 },
    side: { width: 128, height: (128 * 520) / 600 },
    contentHeight: 176,
    minimumReadableShortSide: 80,
    fallbackLayout: 'banner',
  },
  regular: {
    banner: { width: 384, height: (384 * 350) / 1200 },
    side: { width: 144, height: (144 * 520) / 600 },
    contentHeight: 176,
    minimumReadableShortSide: 64,
    fallbackLayout: 'side',
  },
};

export function normalizeCtaImageAspectRatio(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function displayedImageSize(aspectRatio: number, slot: CtaImageSlotGeometry): CtaImageSlotGeometry {
  const slotAspectRatio = slot.width / slot.height;
  if (aspectRatio >= slotAspectRatio) {
    return { width: slot.width, height: slot.width / aspectRatio };
  }
  return { width: slot.height * aspectRatio, height: slot.height };
}

function cardFootprint(layout: CtaImageLayout, geometry: CtaImageCardGeometry): number {
  const cardWidth = geometry.banner.width;
  const height =
    layout === 'banner'
      ? geometry.banner.height + geometry.contentHeight
      : Math.max(geometry.side.height, geometry.contentHeight);
  return cardWidth * height;
}

/** Chooses the smallest readable card while keeping the complete image visible with `contain`. */
export function resolveCtaImageLayout(
  aspectRatio: number | null | undefined,
  target: CtaImageCardTarget
): CtaImageLayout {
  const normalizedAspectRatio = normalizeCtaImageAspectRatio(aspectRatio);
  const geometry = CARD_GEOMETRY[target];
  if (normalizedAspectRatio === null) return geometry.fallbackLayout;

  const candidates = (['banner', 'side'] as const).map((layout) => {
    const displayed = displayedImageSize(normalizedAspectRatio, geometry[layout]);
    return {
      layout,
      displayedShortSide: Math.min(displayed.width, displayed.height),
      footprint: cardFootprint(layout, geometry),
    };
  });
  const readable = candidates.filter(
    (candidate) => candidate.displayedShortSide >= geometry.minimumReadableShortSide
  );

  if (readable.length > 0) {
    return readable.sort(
      (left, right) =>
        left.footprint - right.footprint ||
        right.displayedShortSide - left.displayedShortSide ||
        left.layout.localeCompare(right.layout)
    )[0].layout;
  }

  return candidates.sort(
    (left, right) =>
      right.displayedShortSide - left.displayedShortSide ||
      left.footprint - right.footprint ||
      left.layout.localeCompare(right.layout)
  )[0].layout;
}

/**
 * Raster uploads persist source geometry only after both variants finish uploading.
 * Legacy images lack stored geometry, and SVG uploads never create raster variants.
 * Dimensions measured by the card select its layout but do not prove variants exist.
 */
export function getCtaImageVariantPaths(
  storagePath: string,
  storedAspectRatio: number | null | undefined
): [string, string] | null {
  if (
    normalizeCtaImageAspectRatio(storedAspectRatio) === null ||
    !/\.(png|jpg|webp)$/i.test(storagePath)
  ) {
    return null;
  }

  const stem = storagePath.slice(0, storagePath.lastIndexOf('.'));
  return [`${stem}.mobile.webp`, `${stem}.desktop.webp`];
}
